import { reorderGroups, type Entity, type LibraryGroup } from '@dispositio/core';
import { CATEGORY_COLORS, categoryOf, groupsOfType, growthShift, layoutLibrary, newCategoryFrame, slotInGroup } from '../../lib/libraryModel';
import { useStoryEnv } from '../../state/storyEnv';

/**
 * Kategorien der Bibliothek bedienen. Eine Kategorie ist ein Rahmen der Pinnwand: Wer einen Eintrag hineinlegt, legt ihn auf einen
 * freien Platz in diesem Rahmen (der Rahmen wächst bei Bedarf); ohne Kategorie ordnet die Pinnwand ihn selbst unterhalb der Rahmen an.
 */
export function useCategoryActions() {
  const { ctx, actions } = useStoryEnv();

  /** Eintrag in eine Kategorie legen bzw. mit `null` aus jeder herausnehmen. Der Eintrag darf neu sein (noch nicht im Kontext). */
  const moveIntoNow = async (entity: Entity, groupId: string | null): Promise<boolean> => {
    const groups = groupsOfType(ctx, entity.typeId);
    const layout = layoutLibrary(ctx);
    const here = layout.find((p) => p.entity.id === entity.id);
    const current = here ? categoryOf(groups, here) : null;
    if ((current?.id ?? null) === groupId) return true;
    if (groupId === null) return !!(await actions.updateEntity(entity.id, { x: null, y: null }));
    const group = groups.find((g) => g.id === groupId);
    if (!group) return false;
    const others = groups.filter((g) => g.id !== group.id);
    const taken = layout.filter((p) => p.entity.id !== entity.id && p.entity.typeId === entity.typeId && categoryOf(groups, p)?.id === group.id);
    // Rahmen darunter rücken beim Wachsen nach; nur Rahmen, die schon in diesen hineinragen, sperren Plätze
    const slot = slotInGroup(group, taken, others.filter((g) => g.y < group.y + group.h));
    if (slot.w !== group.w || slot.h !== group.h) {
      // wächst der Rahmen nach unten, rückt alles darunter nach (Rahmen samt Karten), damit nichts überdeckt wird
      const shift = growthShift(group, slot.h, others, layout.filter((p) => p.entity.typeId === entity.typeId && p.entity.id !== entity.id));
      await Promise.all([
        actions.updateGroup(group.id, { w: slot.w, h: slot.h }),
        ...shift.frames.map((f) => actions.updateGroup(f.id, { y: f.y })),
        ...shift.cards.map((c) => actions.updateEntity(c.id, { y: c.y })),
      ]);
    }
    return !!(await actions.updateEntity(entity.id, { x: slot.x, y: slot.y }));
  };

  /** Neue Kategorie eines Bereichs: ein Rahmen unter allem, was der Bereich auf der Pinnwand schon hat, Farbe der Reihe nach. */
  const create = async (typeId: string, name: string): Promise<LibraryGroup | null> => {
    const groups = groupsOfType(ctx, typeId);
    const placed = layoutLibrary(ctx).filter((p) => p.placed && p.entity.typeId === typeId);
    const frame = newCategoryFrame(groups, placed);
    const cs = await actions.createGroup({ name, typeId, color: CATEGORY_COLORS[groups.length % CATEGORY_COLORS.length]!, ...frame });
    return cs?.groups[0] ?? null;
  };

  /** Kategorie in der Kartenansicht vor `beforeId` setzen (null: ans Ende); meist bekommt nur sie einen neuen Sortierschlüssel. */
  const reorder = async (typeId: string, groupId: string, beforeId: string | null): Promise<void> => {
    const updates = reorderGroups(groupsOfType(ctx, typeId), groupId, beforeId);
    // im Verlauf ein Schritt
    await actions.group(() => Promise.all(updates.map((u) => actions.updateGroup(u.id, { rank: u.rank }))));
  };
  /** Karte in eine Kategorie legen (wächst der Rahmen, rückt alles darunter nach) – im Verlauf ein Schritt */
  const moveInto = (entity: Entity, groupId: string | null): Promise<boolean> => actions.group(() => moveIntoNow(entity, groupId));

  return { moveInto, create, reorder };
}
